/**
 * CrawlStrategyPanel — Collapsible panel for viewing/editing light sources and stealth mode.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Flame, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CrawlStrategy } from '@omen-quest/stretch-engine';

interface CrawlStrategyPanelProps {
  strategy: CrawlStrategy;
  onToggleStealth: () => void;
  className?: string;
}

export function CrawlStrategyPanel({ strategy, onToggleStealth, className }: CrawlStrategyPanelProps) {
  const [expanded, setExpanded] = useState(false);

  const activeLights = strategy.lightSources.filter(s => s.remainingStretches > 0);

  return (
    <div className={cn('border border-white/5 rounded-lg bg-neutral-900/50', className)}>
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full px-3 py-2 text-xs text-neutral-400 hover:text-white transition-colors"
      >
        <span className="font-medium">Crawl Strategy</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-white/5">
          {/* Light sources */}
          <div className="space-y-1.5 mt-2">
            <span className="text-xs text-neutral-500 font-medium">Light Sources</span>
            {activeLights.length === 0 ? (
              <p className="text-xs text-red-400/70">No active light sources — darkness!</p>
            ) : (
              activeLights.map(source => (
                <div key={source.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <Flame className={cn(
                      'w-3.5 h-3.5',
                      source.remainingStretches <= 2 ? 'text-red-400' : 'text-yellow-400',
                    )} />
                    <span className="text-neutral-300 capitalize">{source.type}</span>
                  </div>
                  <span className={cn(
                    source.remainingStretches <= 2 ? 'text-red-400' :
                    source.remainingStretches <= 4 ? 'text-amber-400' :
                    'text-neutral-500',
                  )}>
                    {source.remainingStretches === Infinity ? 'Permanent' : `${source.remainingStretches} left`}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Stealth mode */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs">
              <EyeOff className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-neutral-300">Stealth Mode</span>
            </div>
            <button
              onClick={onToggleStealth}
              className={cn(
                'px-2 py-0.5 text-xs rounded border transition-colors',
                strategy.stealthMode
                  ? 'bg-purple-900/40 border-purple-700/50 text-purple-300'
                  : 'bg-neutral-800/50 border-white/10 text-neutral-500 hover:text-white',
              )}
            >
              {strategy.stealthMode ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
